import { Capacitor } from '@capacitor/core';
import { CapgoLLM } from '@capgo/capacitor-llm';
import { getSettings, getEntries } from './storage';

const MODEL_COOLDOWN_MS = 10 * 60 * 1000;
let modelUnavailableUntil = 0;

const MODEL_FAILURE_PATTERNS = [
  /modelcatalog/i,
  /unifiedassetframework/i,
  /foundationmodels\.languagemodelsession\.generationerror/i,
  /generationerror\s*error\s*-1/i,
  /failed model manager query/i,
  /consistency token/i,
  /there are no underlying assets/i,
];

const normalizeErrorText = (error) => {
  if (!error) return '';
  if (typeof error === 'string') return error;

  const parts = [
    error.message,
    error.errorMessage,
    error.localizedDescription,
  ].filter(Boolean);

  try {
    parts.push(JSON.stringify(error));
  } catch {
    // Ignore stringify failures for non-serializable error objects.
  }

  return parts.join(' | ');
};

const isModelCatalogFailure = (error) => {
  const text = normalizeErrorText(error).toLowerCase();
  if (!text) return false;
  return MODEL_FAILURE_PATTERNS.some((pattern) => pattern.test(text));
};

const markModelUnavailable = (error) => {
  modelUnavailableUntil = Date.now() + MODEL_COOLDOWN_MS;
  console.warn('Apple model unavailable, entering cooldown:', normalizeErrorText(error));
};

const canUseNativeModel = () => Date.now() >= modelUnavailableUntil;

export const isNativeiOS = () => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
};

export const isIOSSimulator = () => {
  if (!isNativeiOS()) return false;
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return /Simulator/i.test(userAgent);
};

export const isAppleIntelligenceSupported = async () => {
  if (!isNativeiOS()) return false;
  if (isIOSSimulator()) return false;
  if (!canUseNativeModel()) return false;
  try {
    const result = await CapgoLLM.getReadiness();
    return result.readiness === 'ready';
  } catch (error) {
    if (isModelCatalogFailure(error)) {
      markModelUnavailable(error);
    }
    console.log('Apple Intelligence not available:', error);
    return false;
  }
};

const sendAndGetResponse = async (chatId, message, timeoutMs = 30000) => {
  return new Promise((resolve, reject) => {
    let latestResponse = '';
    let textListener = null;
    let finishListener = null;
    let settled = false;

    const cleanup = async () => {
      if (textListener) {
        await textListener.remove();
      }
      if (finishListener) {
        await finishListener.remove();
      }
    };

    const safeResolve = async (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      await cleanup();
      resolve(value);
    };

    const safeReject = async (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      await cleanup();
      reject(error);
    };

    const timeout = setTimeout(() => {
      void safeReject(new Error('AI response timeout'));
    }, timeoutMs);

    Promise.all([
      CapgoLLM.addListener('textFromAi', (event) => {
        if (event.chatId === chatId && event.text) {
          latestResponse = event.text;
        }
      }),
      CapgoLLM.addListener('aiFinished', (event) => {
        if (event.chatId === chatId) {
          // Slight grace period allows last snapshot chunk to land before resolve.
          setTimeout(() => {
            void safeResolve(latestResponse);
          }, 120);
        }
      }),
    ])
      .then(async ([textSub, finishSub]) => {
        textListener = textSub;
        finishListener = finishSub;
        await CapgoLLM.sendMessage({ chatId, message });
      })
      .catch((error) => {
        void safeReject(error);
      });
  });
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForReadiness = async (attempts = 3, delayMs = 900) => {
  if (!canUseNativeModel()) return false;

  for (let i = 0; i < attempts; i++) {
    try {
      const readiness = await CapgoLLM.getReadiness();
      if (readiness.readiness === 'ready') {
        return true;
      }
    } catch (error) {
      if (isModelCatalogFailure(error)) {
        markModelUnavailable(error);
        return false;
      }
    }

    if (i < attempts - 1) {
      await wait(delayMs);
    }
  }

  return false;
};

const cleanAIResponse = (response, maxWords = null) => {
  if (!response) return '';
  response = response.trim().replace(/^["']|["']$/g, '');
  response = response.replace(/\.+/g, '.').replace(/\s+/g, ' ').trim();

  if (maxWords) {
    const words = response.split(/\s+/);
    if (words.length > maxWords) {
      response = words.slice(0, maxWords).join(' ');
      if (!response.match(/[.?"]$/)) response += '.';
    }
  }
  return response;
};

const stripAIArtifacts = (response) => {
  if (!response) return '';

  let cleaned = response
    .replace(/\r/g, '')
    .replace(/\[\s*your\s*name\s*\]/gi, '')
    .replace(/\[\s*name\s*\]/gi, '')
    .replace(/^\s*(warm regards|best regards|regards|sincerely|take care)\s*[,:-]*\s*$/gim, '')
    .replace(/^\s*(warm regards|best regards|regards|sincerely|take care)\s*[,:-].*$/gim, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return cleaned;
};

const uniqueSentences = (text) => {
  const sentences = text
    .replace(/\s*\n+\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const seen = new Set();
  const deduped = [];
  for (const sentence of sentences) {
    const key = sentence.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(sentence);
  }
  return deduped;
};

const trimToWordLimit = (text, maxWords) => {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return text;
  const clipped = words.slice(0, maxWords).join(' ');
  return /[.!?]$/.test(clipped) ? clipped : `${clipped}.`;
};

const sanitizeGreeting = (response) => {
  const cleaned = stripAIArtifacts(cleanAIResponse(response));
  if (!cleaned) return '';

  const unique = [];
  for (const sentence of uniqueSentences(cleaned)) {
    const lower = sentence.toLowerCase();
    if (lower.includes("here's a warm greeting for you")) continue;
    unique.push(sentence);
  }

  if (unique.length === 0) return '';

  // Keep a fuller welcome (about 40 words) by allowing up to 2 unique sentences.
  const firstTwo = unique.slice(0, 2).join(' ');
  return trimToWordLimit(firstTwo, 40);
};

const sanitizeWeeklySummary = (response, userName) => {
  const cleaned = stripAIArtifacts(cleanAIResponse(response));
  if (!cleaned) return '';

  const summary = trimToWordLimit(uniqueSentences(cleaned).join(' '), 80)
    .replace(/\s+/g, ' ')
    .trim();

  if (!summary) return '';
  if (!new RegExp(`\\b${userName}\\b`, 'i').test(summary)) {
    return `Hey ${userName}, ${summary.charAt(0).toLowerCase()}${summary.slice(1)}`;
  }
  return summary;
};

const pickTwoMetrics = (metrics) => {
  const shuffled = [...metrics].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 2);
};

const getUserName = () => {
  const settings = getSettings();
  let name = settings?.name && settings.name.trim() ? settings.name.trim() : '';
  if (!name) return 'friend';
  return name;
};

// ============================================================
// GENERATE WELCOME MESSAGE (Home screen)
// ============================================================
export const generateWelcomeMessage = async (goals) => {
  try {
    const userName = getUserName();

    const ready = await waitForReadiness(3, 1000);
    if (!ready) {
      return { success: true, response: `Hello ${userName}` };
    }

    const goalsText = goals.slice(0, 4).map(g => `- ${g.text}`).join('\n');

    const allEntries = getEntries();
    const recentEntries = Object.entries(allEntries)
      .map(([date, entry]) => ({ date, entry }))
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 14);

    const checkins14 = recentEntries.length;
    const goalWins14 = recentEntries.reduce((count, item) => {
      const statuses = Object.values(item.entry?.goals || {});
      return count + statuses.filter((s) => s?.completed || s?.easyActivity).length;
    }, 0);
    const reflectionDays14 = recentEntries.reduce((count, item) => {
      const hasReflection = Object.keys(item.entry || {}).some((k) =>
        k.startsWith('answer') && item.entry[k] && item.entry[k] !== '–' && item.entry[k].trim().length > 0
      );
      return count + (hasReflection ? 1 : 0);
    }, 0);

    const prompt = `The user's name is ${userName}. Write ONE warm personalized greeting between 28 and 40 words. Address them directly with you/your and include their name once. Mention one current goal and one brief progress cue from recent history.

Recent history (last 14 days):
- check-ins: ${checkins14}
- positive goal wins: ${goalWins14}
- reflection days: ${reflectionDays14}

User's goals:
${goalsText}

Rules:
- No lists, no markdown, no asterisks, no quotes
- No sign-off lines (no "Warm regards", no "[Your Name]")
- Keep it to one compact paragraph

Write your response:`;

    const chat = await CapgoLLM.createChat();
    const response = await sendAndGetResponse(chat.id, prompt, 15000);
    let cleaned = sanitizeGreeting(response);
    
    // Remove any asterisks or special characters
    cleaned = cleaned.replace(/\*/g, '');
    cleaned = cleaned.replace(/"/g, '');

    if (!cleaned) {
      const topGoal = goals[0]?.text || 'your next small step';
      const fallback = `Hi ${userName}, your consistency is building real momentum. Keep going with ${topGoal} today, and use your recent progress as proof that each small action is moving you forward in the right direction.`;
      return { success: true, response: trimToWordLimit(fallback, 40) };
    }

    if (!new RegExp(`\\b${userName}\\b`, 'i').test(cleaned)) {
      cleaned = `Hi ${userName}, ${cleaned.charAt(0).toLowerCase()}${cleaned.slice(1)}`;
    }

    return { success: true, response: trimToWordLimit(cleaned, 40) };
  } catch (error) {
    if (isModelCatalogFailure(error)) {
      markModelUnavailable(error);
    }
    const userName = getUserName();
    return { success: true, response: `Hello ${userName}` };
  }
};

// ============================================================
// GENERATE DAILY RESPONSE
// ============================================================
export const generateAIResponse = async (formData, goals, prompts) => {
  try {
    const userName = getUserName();

    const ready = await waitForReadiness(3, 1000);
    if (!ready) {
      return { success: false, error: 'LLM not ready' };
    }

    const goalStatuses = goals.map(goal => {
      const status = formData.goals?.[goal.id];
      let text = 'not completed';
      if (status?.completed && status?.easyActivity) text = 'completed full + easy';
      else if (status?.completed) text = 'completed';
      else if (status?.easyActivity) text = 'did easy version';
      return `- ${goal.text}: ${text}`;
    }).join('\n');

    const reflections = prompts.map(p => {
      const answer = formData.answers?.[p.answerKey] || '';
      return answer ? `- ${p.question}: ${answer}` : '';
    }).filter(Boolean).join('\n');

    const prompt = `The user's name is ${userName}. Write ONE warm motivational sentence (max 25 words) directly to them. Use second person (you/your) and include their name naturally, for example: "Well done ${userName}, you...". Never refer to them in third person. No asterisks, no special formatting. Just plain text.

Today's entry:
${goalStatuses}
${reflections}

Write your response:`;

    const chat = await CapgoLLM.createChat();
    const response = await sendAndGetResponse(chat.id, prompt);
    let cleaned = sanitizeGreeting(response);
    if (!cleaned) {
      cleaned = cleanAIResponse(response, 25);
    }
    cleaned = cleaned.replace(/\*/g, '');
    cleaned = cleaned.replace(/"/g, '');

    return { success: true, response: cleaned };
  } catch (error) {
    if (isModelCatalogFailure(error)) {
      markModelUnavailable(error);
      return { success: false, error: 'LLM model assets unavailable' };
    }
    return { success: false, error: error.message };
  }
};

// Generate daily completion text with a single centralized AI fallback path.
export const getDailyCompletionMessage = async ({ formData, goals, prompts, fallbackMessage }) => {
  try {
    const result = await generateAIResponse(formData, goals, prompts);
    if (result.success && result.response) {
      return { message: result.response, usedAI: true };
    }
    return { message: fallbackMessage, usedAI: false };
  } catch {
    return { message: fallbackMessage, usedAI: false };
  }
};

// ============================================================
// GENERATE WEEKLY SUMMARY
// ============================================================
export const generateWeeklySummary = async (entries) => {
  try {
    const userName = getUserName();

    if (!canUseNativeModel()) {
      return { success: false, error: 'LLM not ready' };
    }
    
    const readiness = await CapgoLLM.getReadiness();
    if (readiness.readiness !== 'ready') {
      return { success: false, error: 'LLM not ready' };
    }

    const settings = getSettings();
    const normalizedEntries = entries.slice(0, 7).map((item) => {
      const entry = item.entry || item;
      const date = item.date || entry.date;
      return { date, entry };
    });

    const last7Days = new Set();
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      last7Days.add(d.toISOString().split('T')[0]);
    }

    const loggedInWeek = normalizedEntries.filter((item) => item.date && last7Days.has(item.date)).length;
    const goalWins = normalizedEntries.reduce((count, item) => {
      const entry = item.entry;
      const statuses = Object.values(entry.goals || {});
      return count + statuses.filter((s) => s?.completed || s?.easyActivity).length;
    }, 0);
    const reflectionWins = normalizedEntries.reduce((count, item) => {
      const entry = item.entry;
      const promptDefs = entry.promptSnapshots || [];
      const hasReflection = promptDefs.length > 0
        ? promptDefs.some((p) => {
            const value = entry[p.answerKey];
            return value && value !== '–' && value.trim().length > 0;
          })
        : false;
      return count + (hasReflection ? 1 : 0);
    }, 0);

    const entrySummaries = normalizedEntries.map((item) => {
      const date = item.date;
      const entry = item.entry;
      const completed = (settings?.goals || []).filter(goal => {
        const status = entry.goals?.[goal.id];
        return status?.completed || status?.easyActivity;
      }).map(goal => goal.text).join(', ');
      return `${date}: ${completed || 'No goals'}`;
    }).join('\n');

    const metricPool = [
      { key: 'checkins', line: `- Check-ins this week: ${loggedInWeek}` },
      { key: 'wins', line: `- Positive goal wins: ${goalWins}` },
      { key: 'reflections', line: `- Reflection days: ${reflectionWins}` },
    ];
    const selectedMetrics = pickTwoMetrics(metricPool);

      const prompt = `The user's name is ${userName}. Write one compassionate motivational weekly summary (max 80 words) directly to them in second person (you/your).

Use exactly these TWO metrics and do not mention any third metric:
${selectedMetrics.map((m) => m.line).join('\n')}

Make it warm, specific, and encouraging. Mention momentum and one next step. Do not use placeholders like [Your Name]. Do not add sign-offs. No asterisks, no special formatting. Just plain text.

Last 7 days:
${entrySummaries}

Write your response:`;

    const chat = await CapgoLLM.createChat();
    const response = await sendAndGetResponse(chat.id, prompt);
    let cleaned = sanitizeWeeklySummary(response, userName);
    cleaned = cleaned.replace(/\*/g, '');
    cleaned = cleaned.replace(/"/g, '');

    if (!cleaned) {
      const selectedLines = selectedMetrics.map((m) => m.line.replace('- ', '').toLowerCase());
      cleaned = `Hey ${userName}, this week you made steady progress with ${selectedLines.join(' and ')}. Keep your momentum going by choosing one clear next step for tomorrow and following through.`;
      cleaned = trimToWordLimit(cleaned, 80);
    }

    return { success: true, response: cleaned };
  } catch (error) {
    if (isModelCatalogFailure(error)) {
      markModelUnavailable(error);
      return { success: false, error: 'LLM model assets unavailable' };
    }
    return { success: false, error: error.message };
  }
};

// ============================================================
// SUGGEST EASY ACTIVITY
// ============================================================
export const suggestEasyActivity = async (goalText) => {
  try {
    if (!canUseNativeModel()) {
      return { success: false, error: 'LLM not ready' };
    }

    const readiness = await CapgoLLM.getReadiness();
    if (readiness.readiness !== 'ready') {
      return { success: false, error: 'LLM not ready' };
    }

    const chat = await CapgoLLM.createChat();
    const message = `The user wants to do: "${goalText}". Suggest ONE simple easy version (max 8 words). No asterisks, no special formatting:`;
    const response = await sendAndGetResponse(chat.id, message, 15000);
    let cleaned = response.trim().replace(/^["']|["']$/g, '');
    cleaned = cleaned.replace(/\*/g, '');
    if (cleaned.split(/\s+/).length > 8) cleaned = cleaned.split(/\s+/).slice(0, 8).join(' ');
    return { success: true, suggestion: cleaned };
  } catch (error) {
    if (isModelCatalogFailure(error)) {
      markModelUnavailable(error);
      return { success: false, error: 'LLM model assets unavailable' };
    }
    return { success: false, error: error.message };
  }
};

export default {
  isNativeiOS,
  isAppleIntelligenceSupported,
  generateAIResponse,
  getDailyCompletionMessage,
  generateWeeklySummary,
  suggestEasyActivity,
  generateWelcomeMessage,
};